<template>
  <footer 
    class="bg-[#0f4640] text-[#faf6df] py-16"
    :dir="$i18n.locale === 'ar' ? 'rtl' : 'ltr'"
  >
    <!-- Main Footer -->
    <div class="container mx-auto px-4 xl:px-0">
      <div class="grid">
        <!-- Company Info -->
        <div class="space-y-6">
          <div class="logo-container">
            <img 
              src="@/assets/logo.png" 
              :alt="$i18n.locale === 'ar' ? 'سلاسة' : 'Salasah'" 
              class="h-10 w-auto"
              width="120"
              height="40"
              loading="lazy"
            >
          </div>
          <p class="footer-description text-[#faf6df]/80 text-sm leading-relaxed max-w-sm">
            {{ $t('footer.description') }}
          </p>
          <div class="social-links flex gap-4">
            <a 
              v-for="(social, index) in socialLinks"
              :key="index"
              :href="social.url" 
              :aria-label="social.label"
              class="bg-[#faf6df]/10 p-3 rounded-xl hover:bg-[#faf6df] hover:text-[#0f4640] transition-all duration-300"
              target="_blank"
              rel="noopener noreferrer"
            >
              <i :class="[social.icon, 'text-lg']"></i>
            </a>
          </div>
        </div>

        <!-- Quick Links -->
        <nav class="space-y-6">
          <h3 class="section-title">{{ $t('footer.quickLinks.title') }}</h3>
          <ul class="space-y-4">
            <li v-for="link in quickLinks" :key="link.path">
              <router-link 
                :to="link.path" 
                class="text-[#faf6df]/80 hover:text-[#faf6df] transition-all duration-300 block py-1"
              >
                {{ $t(link.titleKey) }}
              </router-link>
            </li>
          </ul>
        </nav>

        <!-- Support -->
        <nav class="space-y-6">
          <h3 class="section-title">{{ $t('footer.support.title') }}</h3>
          <ul class="space-y-4">
            <li v-for="link in supportLinks" :key="link.path">
              <router-link 
                :to="link.path" 
                class="text-[#faf6df]/80 hover:text-[#faf6df] transition-all duration-300 block py-1"
              >
                {{ $t(link.titleKey) }}
              </router-link>
            </li>
          </ul>
        </nav>

        <!-- Contact Info -->
        <div class="contact-info space-y-6">
          <h3 class="section-title">{{ $t('footer.contact.title') }}</h3>
          <div class="space-y-4">
            <a 
              :href="'mailto:' + contactInfo.email" 
              class="text-[#faf6df] hover:bg-[#1f9285] hover:text-[#faf6df] transition-all duration-300 flex items-center gap-4 bg-[#faf6df]/10 p-4 rounded-xl"
            >
              <i class="far fa-envelope text-xl"></i>
              <span>{{ contactInfo.email }}</span>
            </a>
            <a 
              :href="'tel:' + contactInfo.phone" 
              class="text-[#faf6df] hover:bg-[#1f9285] hover:text-[#faf6df] transition-all duration-300 flex items-center gap-4 bg-[#faf6df]/10 p-4 rounded-xl"
            >
              <i class="fas fa-phone-alt text-lg"></i>
              <span dir="ltr">{{ contactInfo.phone }}</span>
            </a>
          </div>
        </div>
      </div>

      <!-- Bottom Bar -->
      <div class="bottom-bar">
        <div class="flex flex-col md:flex-row items-center justify-between gap-6">
          <div class="text-[#faf6df]/80 text-sm order-2 md:order-1">
            {{ $t('footer.copyright') }}
          </div>
          <nav class="bottom-links flex flex-wrap items-center justify-center gap-6 order-1 md:order-2">
            <router-link 
              v-for="link in legalLinks"
              :key="link.path"
              :to="link.path" 
              class="text-[#faf6df]/80 hover:text-[#faf6df] transition-all duration-300"
            >
              {{ $t(link.titleKey) }}
            </router-link>
            <a 
              :href="developerInfo.url" 
              target="_blank" 
              rel="noopener noreferrer" 
              class="text-[#faf6df]/80 hover:text-[#faf6df] transition-all duration-300 flex items-center gap-2"
            >
              {{ $t('footer.developer.prefix') }}
              <span class="font-bold">{{ developerInfo.name }}</span>
            </a>
          </nav>
        </div>
      </div>
    </div>
  </footer>
</template>

<script>
export default {
  name: 'FooterComponent',
  data() {
    return {
      socialLinks: [
        { icon: 'fab fa-instagram', label: 'انستجرام', url: 'https://www.instagram.com/salasah.sa?igsh=ZGUzMzM3NWJiOQ==' },
        { icon: 'fab fa-tiktok', label: 'تيك توك', url: 'https://tiktok.com/@salasah.sa' },
        { icon: 'fab fa-x-twitter', label: 'إكس', url: 'https://x.com/salasah2024' }
      ],
      quickLinks: [
        { path: '/workspace', titleKey: 'navbar.links.workspace' },
        { path: '/financial', titleKey: 'navbar.links.financial' },
        { path: '/hr', titleKey: 'navbar.links.hr' },
        { path: '/manufacturing', titleKey: 'navbar.links.manufacturing' }
      ],
      supportLinks: [
        { path: '/support', titleKey: 'navbar.links.support' },
        { path: '/demo', titleKey: 'navbar.demo' },
        { path: '/contact', titleKey: 'footer.contact.title' }
      ],
      legalLinks: [
        { path: '/privacy', titleKey: 'footer.legal.privacy' },
        { path: '/terms', titleKey: 'footer.legal.terms' }
      ],
      contactInfo: {
        email: 'help@salasah.sa',
        phone: '+966 56 173 0527'
      },
      developerInfo: {
        name: 'تكوين سوفت',
        url: 'https://takweensoft.com'
      }
    }
  }
}
</script>

<style scoped>
:root {
  --primary-color: #0f4640;
  --primary-light: #1f9285;
  --secondary-color: #faf6df;
  --text-muted: rgba(250, 246, 223, 0.8);
  --transition-base: all 0.3s ease;
}

/* Remove underlines and enhance links */
a {
  text-decoration: none;
  transition: var(--transition-base);
  position: relative;
}

/* Enhanced hover effect for links */
a:not(.router-link-exact-active):hover {
  color: var(--secondary-color);
  transform: translateY(-2px);
}

/* Social media icons hover effect */
.social-links a:hover {
  background-color: var(--secondary-color);
  color: var(--primary-color);
  transform: translateY(-3px);
}

/* Footer grid layout */
.grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
}

/* Enhanced contact buttons */
.contact-info a {
  border: 1px solid rgba(250, 246, 223, 0.1);
  transition: all 0.3s ease;
}

.contact-info a:hover {
  background: var(--primary-light);
  border-color: var(--primary-light);
  transform: translateY(-3px);
}

/* Bottom bar enhancements */
.bottom-bar {
  border-top: 1px solid rgba(250, 246, 223, 0.1);
  padding-top: 2rem;
  margin-top: 3rem;
}

/* RTL Support */
[dir="rtl"] {
  font-family: "Cairo", sans-serif;
}

[dir="ltr"] {
  font-family: system-ui, -apple-system, sans-serif;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
  }
  
  .flex-col {
    text-align: center;
  }
  
  [dir="rtl"] .flex-col {
    text-align: center;
  }
  
  .contact-info {
    grid-column: span 2;
  }
}

@media (max-width: 480px) {
  .grid {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
  
  .contact-info {
    grid-column: span 1;
  }
}

/* Animation for social icons */
@keyframes pulse {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
}

.social-links a:hover i {
  animation: pulse 0.6s ease-in-out;
}

/* Enhanced bottom links */
.bottom-links a {
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  transition: all 0.3s ease;
}

.bottom-links a:hover {
  background: rgba(250, 246, 223, 0.1);
}

/* Logo hover effect */
.logo-container {
  transition: all 0.3s ease;
}

.logo-container:hover {
  transform: scale(1.05);
}

/* Footer description text */
.footer-description {
  line-height: 1.6;
  opacity: 0.9;
}

/* Enhanced section titles */
.section-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  position: relative;
}

.section-title::after {
  content: '';
  position: absolute;
  bottom: -0.5rem;
  left: 0;
  width: 2rem;
  height: 2px;
  background: var(--primary-light);
  transition: width 0.3s ease;
}

[dir="rtl"] .section-title::after {
  left: auto;
  right: 0;
}

.section-title:hover::after {
  width: 3rem;
}
</style>